import {IconCaret, IconMinus, IconPlus} from '~/components';
import {Drawer} from './Drawer.client';
import {Image, Link, useNavigate} from '@shopify/hydrogen';
import {useCallback, useState} from 'react';
import {
  MegaMenuColumnEntity,
  MegaMenuColumnGroupEntity,
  MenuLinkEntity,
} from '~/types';
import clsx from 'clsx';
// @ts-expect-error @headlessui/react incompatibility with node16 resolution
import {Dialog} from '@headlessui/react';
import iconClose from '../../assets/icons/icon-close.svg';
import {useMeasure} from 'react-use';
import SanityImage from '../media/SanityImage.client';
import sanityConfig from '../../../sanity.config';

export function MenuDrawer({
  isOpen,
  onClose,
  menuLinks,
}: {
  isOpen: boolean;
  onClose: () => void;
  menuLinks: MenuLinkEntity[];
}) {
  const [activeMenu, setActiveMenu] = useState<MenuLinkEntity | null>(null);
  const [ref, bounds] = useMeasure<HTMLDivElement>();
  const navigate = useNavigate();

  const onCloseCallback = useCallback(() => {
    onClose();
    setActiveMenu(null);
  }, [onClose]);

  const onClickCallback = useCallback(
    (menu: MenuLinkEntity) => {
      if (menu.megaMenuLinks) {
        setActiveMenu(menu);
      } else {
        navigate(menu.href ?? '#');
        onCloseCallback();
      }
    },
    [navigate, onCloseCallback, setActiveMenu],
  );

  return (
    <Drawer
      classNamePanel={'max-w-lg'}
      open={isOpen}
      onClose={onCloseCallback}
      openFrom="left"
      hideHeader
    >
      <header
        className={`sticky top-0 flex items-center mr-[6px] pt-5 z-20 bg-white ${
          activeMenu ? 'justify-between' : 'justify-end'
        }`}
      >
        {activeMenu && (
          <Dialog.Title
            className="flex items-center cursor-pointer"
            onClick={() => setActiveMenu(null)}
          >
            <IconCaret direction="right" className="w-12 h-[46.5px]" />

            <span className="text-18/26 font-header font-bold tracking-wider">
              {activeMenu.name}
            </span>
          </Dialog.Title>
        )}
        <button
          type="button"
          className="w-12 h-12 rounded-full flex justify-center items-center outline-none"
          onClick={onCloseCallback}
        >
          <Image
            loading="lazy"
            src={iconClose}
            width={17}
            height={17}
            alt="Close modal"
          />
        </button>
      </header>

      <div className="flex py-3 overflow-x-hidden" ref={ref}>
        <nav
          className={clsx('flex-shrink-0 transition-all max-w-full w-[32rem]')}
          style={{
            marginLeft: activeMenu ? `-${bounds.width}px` : 0,
            height: activeMenu ? 0 : 'auto',
          }}
        >
          {menuLinks.map((menu) => (
            <button
              key={menu.name}
              type="button"
              className={clsx(
                'font-header font-bold text-28/38 tracking-wider flex justify-between pl-10 pr-[30px] first:border-t first:border-t-gray-50 border-b border-b-gray-50 py-2 cursor-pointer w-full',
              )}
              style={{
                color: menu.text?.colorOption ?? 'black',
              }}
              onClick={() => onClickCallback(menu)}
            >
              {menu.name}
              <div className="-mt-1">
                <IconCaret
                  direction="left"
                  className={clsx('w-12 h-[46.5px]', {
                    invisible: !menu.megaMenuLinks,
                  })}
                />
              </div>
            </button>
          ))}
        </nav>

        {activeMenu && (
          <nav
            className={clsx(
              'flex-shrink-0 transition-all max-w-full w-[32rem]',
            )}
          >
            <div className="pb-32">
              <div className="flex flex-col">
                {activeMenu.megaMenuLinks?.columns.map((column) => (
                  <MobileMenu
                    key={column.name}
                    column={column}
                    onClose={onCloseCallback}
                  />
                ))}
              </div>

              <div className="pl-10 pr-[30px] mt-5">
                <ul className="font-header text-20/26 font-bold space-y-[19px]">
                  {activeMenu.megaMenuLinks?.leftLinks?.map((leftLink) => (
                    <li
                      key={leftLink.title}
                      style={{
                        color: leftLink.color?.hex,
                      }}
                    >
                      <Link
                        to={leftLink.href ?? '#'}
                        className="hover:underline"
                        onClick={onCloseCallback}
                      >
                        {leftLink.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pl-10 pr-10 mt-10">
                {activeMenu.megaMenuLinks?.image &&
                  activeMenu.megaMenuLinks?.image.asset && (
                    <div className="w-full aspect-square">
                      <Link
                        to={activeMenu.megaMenuLinks?.link ?? ''}
                        onClick={onCloseCallback}
                      >
                        <SanityImage
                          dataset={sanityConfig.dataset}
                          layout="responsive"
                          objectFit="cover"
                          projectId={sanityConfig.projectId}
                          src={activeMenu.megaMenuLinks?.image?.asset?._ref}
                          breakpoints={[[224, 224]]}
                        />
                      </Link>
                    </div>
                  )}
                {activeMenu.megaMenuLinks?.title && (
                  <div className="text-20/26 font-header font-extrabold mt-2.5">
                    <Link
                      to={activeMenu.megaMenuLinks?.link ?? ''}
                      className="hover:underline"
                      onClick={onCloseCallback}
                    >
                      {activeMenu.megaMenuLinks?.title}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </nav>
        )}
      </div>
    </Drawer>
  );
}

function MobileMenu({
  column,
  onClose,
}: {
  column: MegaMenuColumnEntity;
  onClose: () => void;
}) {
  return (
    <div className="last:border-b last:border-b-gray-50">
      {column.groups?.map((group) => (
        <MobileSubMenu key={group.title} group={group} onClose={onClose} />
      ))}
    </div>
  );
}

function MobileSubMenu({
  group,
  onClose,
}: {
  group: MegaMenuColumnGroupEntity;
  onClose: () => void;
}) {
  const [isOpened, setIsOpened] = useState(false);

  return (
    <div
      className={clsx('pl-10 pr-[30px]', {
        'border-t border-t-gray-50': !!group.title,
      })}
    >
      {group.title && (
        <button
          type="button"
          className={clsx(
            'font-header font-bold text-28/38 tracking-wider flex justify-between py-2.5 items-center cursor-pointer w-full',
          )}
          onClick={() => setIsOpened((prev) => !prev)}
        >
          {group.title}
          {isOpened ? (
            <IconMinus className="w-[50px] h-[21px]" />
          ) : (
            <IconPlus className="w-[50px] h-[21px]" />
          )}
        </button>
      )}

      {isOpened && (
        <>
          {group.items?.map((item) => (
            <div key={item.name}>
              {item.title && (
                <div className="font-header text-18/26 font-bold text-gray-400 mt-2.5 tracking-wider">
                  {item.title}
                </div>
              )}
              <ul className="mt-[13px] space-y-2 mb-[33px]">
                {item.items.map((link) => (
                  <li key={link.title}>
                    <Link
                      to={link.href ?? '#'}
                      className="hover:underline"
                      onClick={onClose}
                    >
                      {link.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
